import React from 'react'
import classNames from 'classnames'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles'

import Typography from '@material-ui/core/Typography'
import { SvgIconProps } from '@material-ui/core/SvgIcon'
import Grid, { GridProps } from '@material-ui/core/Grid'
import AddIcon from '@material-ui/icons/Add'

import T from 'components/FormattedMessage'
import SoccerBallIcon from 'components/SoccerBallIcon'
import { UserInfoResponse } from 'protos'
import Button from 'components/Button'
import Badge from 'components/Badge'

import FabButton from './components/FabButton'
import AvatarIcon from './components/AvatarIcon'
import RibbonIcon from './components/RibbonIcon'
import ShieldIcon from './components/ShieldIcon'
import FriendsIcon from './components/FriendsIcon'

const styles = (theme: Theme) => createStyles({
  root: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: theme.palette.grey[300],
    backgroundColor: theme.palette.background.paper,
  },
  icon: {
    color: theme.palette.accent.main,
    verticalAlign: 'middle',
  },
  borderRadiusDisabled: {
    borderRadius: 0,
  },
  fab: {
    position: 'relative',
    marginTop: -6,
  },
  fixed: {
    left: 0,
    right: 0,
    bottom: 0,
    position: 'fixed',
    zIndex: theme.zIndex.appBar,
  },
  button: {
    fontSize: '1rem',
  },
})

interface Props extends GridProps {
  fixed?: boolean
  allFilled?: boolean
  counters?: UserInfoResponse.ICounters | null
}

type ButtonProps = React.ComponentProps<typeof Button>

type InjectedProps = Props & WithStyles<typeof styles> & RouteComponentProps<{}>

const items: Array<{
  to: string,
  title: React.ReactNode,
  IconComponent: React.ComponentType<SvgIconProps & { filled?: boolean }>,
  ButtonComponent: React.ComponentType<ButtonProps> | typeof FabButton,
  getBadgeContent?: (counters?: UserInfoResponse.ICounters | null) => number,
}> = [{
  to: '/ranking',
  title: <T id="root_tab_leaderboard" />,
  IconComponent: ShieldIcon,
  ButtonComponent: Button,
}, {
  to: '/events',
  title: <T id="root_tab_contests" />,
  IconComponent: RibbonIcon,
  ButtonComponent: Button,
  getBadgeContent: counters => (counters?.liveContests ?? 0) + (counters?.pendingFinishedContests ?? 0),
}, {
  to: '/games',
  title: <T id="centerbutton_games" />,
  IconComponent: SoccerBallIcon,
  ButtonComponent: FabButton,
}, {
  to: '/friends',
  title: <T id="root_tab_friends" />,
  IconComponent: FriendsIcon,
  ButtonComponent: Button,
  getBadgeContent: counters => counters?.pendingFriendRequests ?? 0,
}, {
  to: '/profile',
  title: <T id="root_tab_account" />,
  IconComponent: AvatarIcon,
  ButtonComponent: Button,
}]

const BottomNavigation: React.SFC<InjectedProps> = ({
  fixed,
  classes,
  className,
  allFilled,
  location,
  match,
  history,
  counters,
  staticContext,
  ...props
}) => {
  return (
    <Grid
      {...props}
      className={classNames(
        className,
        classes.root,
        { [classes.fixed]: fixed },
      )}
      justify="space-around"
      direction="row"
      wrap="nowrap"
      container
    >
      {items.map(({
        to,
        title,
        IconComponent,
        ButtonComponent,
        getBadgeContent,
      }) => {
        const isFab = ButtonComponent === FabButton
        const badgeContent = getBadgeContent?.(counters)

        if (isFab && location.pathname.startsWith('/games')) {
          to = '/games/create/type'
          title = <T id="centerbutton_play" />
          IconComponent = AddIcon
        }

        // TODO: I'm not sure it is correct, but now it works.
        const isActive = location.pathname.startsWith(to.toString())

        return (
          <Grid key={to.toString()}>
            <ButtonComponent
              to={to}
              className={classNames(classes.button, {
                [classes.borderRadiusDisabled]: !isFab,
                [classes.fab]: isFab,
              })}
            >
              <Grid alignItems="center" direction="column" container>
                <Grid item>
                  <Badge badgeContent={badgeContent} color={(badgeContent && 'primary') || undefined}>
                    {isFab ? (
                      <IconComponent className={classes.icon} />
                    ) : (
                      <IconComponent className={classes.icon} filled={allFilled || isActive} />
                    )}
                  </Badge>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" align="center">
                    {title}
                  </Typography>
                </Grid>
              </Grid>
            </ButtonComponent>
          </Grid>
        )
      })}
    </Grid>
  )
}

export default withStyles(styles)(withRouter(BottomNavigation))
